// feature/command-palette — global Cmd/Ctrl+K palette: search cases by radicado / accionante / case_number.
// feature/notion-polish — also surfaces navigation actions (Ir a…, toggle theme, log out).
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { cases } from "../api/endpoints";
import { useCaseLauncher } from "./CaseLauncher";
import { useDebounced } from "./useDebounced";
import { TipoPill, EstadoPill } from "./Pills";
import { useAuth } from "../auth/AuthContext";

const css = `
.cmdk-backdrop { position: fixed; inset: 0; background: rgba(26,26,26,0.4); backdrop-filter: blur(4px); -webkit-backdrop-filter: blur(4px); z-index: 1000; display: flex; align-items: flex-start; justify-content: center; padding-top: 12vh; }
[data-theme="dark"] .cmdk-backdrop { background: rgba(0,0,0,0.6); }
.cmdk-card { width: min(640px, 92vw); background: var(--glass-bg); backdrop-filter: var(--glass-blur); -webkit-backdrop-filter: var(--glass-blur); border: 1px solid var(--glass-border); border-radius: 16px; box-shadow: var(--shadow-level-3); overflow: hidden; z-index: 1001; }
.cmdk-input { width: 100%; height: 56px; padding: 0 18px; border: none; border-bottom: 1px solid var(--border-hairline); font-size: 16px; outline: none; background: transparent; color: var(--text-primary); border-radius: 0; }
.cmdk-list  { max-height: 60vh; overflow: auto; padding: 8px; }
.cmdk-item  { padding: 10px 12px; border-radius: 8px; cursor: pointer; display: flex; align-items: center; gap: 12px; }
.cmdk-item:hover, .cmdk-item.active { background: var(--bg-subtle); }
.cmdk-hint  { padding: 8px 14px; font-size: 11px; color: var(--text-hint); border-top: 1px solid var(--border-hairline); display: flex; gap: 16px; }
.cmdk-key   { padding: 1px 6px; border: 1px solid var(--border-hairline); border-radius: 4px; font-family: ui-monospace, monospace; font-size: 10px; }
`;

export default function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);
  const launcher = useCaseLauncher();
  const dq = useDebounced(q, 200);
  const nav = useNavigate();
  const { user, logout, setTheme } = useAuth();

  // feature/notion-polish — navigation actions, role-gated.
  const ACTIONS = useMemo(() => {
    const role = user?.role;
    const has = (...allowed) => allowed.includes(role);
    return [
      { id: "nav-cases",      label: "Ir a Casos",                  icon: "📁", run: () => nav("/cases"),                  ok: has("admin","reparto") },
      { id: "nav-new-case",   label: "Nuevo caso",                  icon: "＋", run: () => nav("/cases/new"),              ok: has("admin","reparto") },
      { id: "nav-abogado",    label: "Mis casos (abogado)",         icon: "📋", run: () => nav("/abogado"),                ok: has("admin","abogado") },
      { id: "nav-directora",  label: "Panel de la Directora",       icon: "👁",  run: () => nav("/directora"),              ok: has("admin","directora") },
      { id: "nav-devueltos",  label: "Ver Devueltos",               icon: "↩",  run: () => nav("/admin/devueltos"),        ok: has("admin","reparto","directora") },
      { id: "nav-audit",      label: "Auditoría completa",          icon: "🕐", run: () => nav("/admin/audit-log"),        ok: has("admin","directora") },
      { id: "nav-reports",    label: "Reporte mensual",             icon: "📊", run: () => nav("/admin/reports/monthly"),  ok: has("admin","directora") },
      { id: "nav-exports",    label: "Exportar a LITISOFT",         icon: "📤", run: () => nav("/admin/exports"),          ok: has("admin","reparto","insumos","directora") },
      { id: "nav-users",      label: "Gestionar usuarios",          icon: "👥", run: () => nav("/admin/users"),            ok: has("admin") },
      { id: "theme-toggle",   label: "Cambiar modo claro / oscuro", icon: "🌗", run: () => setTheme(user?.theme === "dark" ? "light" : "dark"), ok: !!user },
      { id: "logout",         label: "Cerrar sesión",               icon: "🚪", run: async () => { await logout(); nav("/login"); }, ok: !!user },
    ].filter(a => a.ok);
  }, [user, nav, setTheme, logout]);

  const filteredActions = useMemo(() => {
    if (!q) return ACTIONS;
    const ql = q.toLowerCase();
    return ACTIONS.filter(a => a.label.toLowerCase().includes(ql));
  }, [ACTIONS, q]);

  useEffect(() => {
    function onKey(e) {
      const isCmdK = (e.key === "k" || e.key === "K") && (e.metaKey || e.ctrlKey);
      if (isCmdK) {
        e.preventDefault();
        setOpen(true); setQ(""); setResults([]); setActive(0);
        setTimeout(() => inputRef.current?.focus(), 50);
      } else if (e.key === "Escape" && open) {
        setOpen(false);
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (!dq || dq.length < 2) { setResults([]); return; }
    cases.list({ q: dq }).then(r => {
      setResults((r.data || []).slice(0, 10));
      setActive(0);
    }).catch(() => setResults([]));
  }, [dq, open]);

  function pickCase(c) {
    setOpen(false);
    launcher.open(c.id);
  }
  function pickAction(a) {
    setOpen(false);
    a.run();
  }

  // Combined linear list for arrow-key navigation: actions first, then cases.
  const linear = [
    ...filteredActions.map(a => ({ kind: "action", item: a })),
    ...results.map(c => ({ kind: "case", item: c })),
  ];

  function onKeyDown(e) {
    if (e.key === "ArrowDown") { e.preventDefault(); setActive(i => Math.min(linear.length - 1, i + 1)); }
    if (e.key === "ArrowUp")   { e.preventDefault(); setActive(i => Math.max(0, i - 1)); }
    if (e.key === "Enter" && linear[active]) {
      e.preventDefault();
      const sel = linear[active];
      if (sel.kind === "action") pickAction(sel.item);
      else pickCase(sel.item);
    }
  }

  if (!open) return null;
  return (
    <>
      <style>{css}</style>
      <div className="cmdk-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) setOpen(false); }}>
        <div className="cmdk-card" role="dialog" aria-modal="true">
          <input
            ref={inputRef}
            className="cmdk-input"
            placeholder="Buscar caso por radicado, accionante o número…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKeyDown}
          />
          <div className="cmdk-list">
            {filteredActions.length > 0 && (
              <>
                <div style={{ padding: "8px 12px 4px", fontSize: 11, textTransform: "uppercase", letterSpacing: ".08em", color: "var(--text-hint)" }}>
                  Acciones
                </div>
                {filteredActions.map((a, i) => (
                  <div key={a.id}
                    className={"cmdk-item" + (i === active ? " active" : "")}
                    onMouseEnter={() => setActive(i)}
                    onClick={() => pickAction(a)}>
                    <span style={{ width: 18, textAlign: "center" }}>{a.icon}</span>
                    <div style={{ flex: 1, color: "var(--text-primary)" }}>{a.label}</div>
                  </div>
                ))}
              </>
            )}

            {results.length > 0 && (
              <div style={{ padding: "8px 12px 4px", fontSize: 11, textTransform: "uppercase", letterSpacing: ".08em", color: "var(--text-hint)" }}>
                Casos
              </div>
            )}
            {results.map((c, i) => {
              const idx = filteredActions.length + i;
              return (
                <div key={c.id}
                  className={"cmdk-item" + (idx === active ? " active" : "")}
                  onMouseEnter={() => setActive(idx)}
                  onClick={() => pickCase(c)}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 500, color: "var(--text-primary)" }}>{c.case_number}</div>
                    <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
                      {c.accionante_nombre || "—"} · {c.radicado}
                    </div>
                  </div>
                  <TipoPill tipo={c.tipo} />
                  <EstadoPill c={c} />
                </div>
              );
            })}

            {!filteredActions.length && !results.length && (
              <div style={{ padding: 20, color: "var(--text-muted)", fontSize: 13 }}>
                {dq ? "Sin coincidencias." : "Empiece a escribir para buscar acciones o casos."}
              </div>
            )}
          </div>
          <div className="cmdk-hint">
            <span><span className="cmdk-key">↑</span> <span className="cmdk-key">↓</span> moverse</span>
            <span><span className="cmdk-key">↵</span> abrir</span>
            <span><span className="cmdk-key">Esc</span> cerrar</span>
          </div>
        </div>
      </div>
    </>
  );
}
